import { TextField } from "@mui/material";
import format from "date-fns/format";
import { useEffect, useRef, useState } from "react";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { getGraphDate } from "util/date";
import "./DateRangePicker.css";

const DateRangePicker = ({ onChange, className }) => {
  // Get start of current month and today's date
  const getDefaultRange = () => {
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    return {
      startDate: startOfMonth,
      endDate: today,
      key: "selection",
    };
  };

  const defaultRange = getDefaultRange();

  const [isExplicitlySet, setIsExplicitlySet] = useState(false);
  const [hasStartedSelection, setHasStartedSelection] = useState(false);
  // Calendar display range - starts empty (no pre-selection)
  const [range, setRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: "selection",
    },
  ]);

  // Call onChange with default range on mount (for API filtering)
  useEffect(() => {
    onChange({
      from: getGraphDate(defaultRange.startDate),
      to: getGraphDate(defaultRange.endDate),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // open close
  const [open, setOpen] = useState(false);

  // get the target element to toggle
  const wrapperRef = useRef(null);
  const calendarRef = useRef(null);

  useEffect(() => {
    if (open) {
      // Only attach listeners when calendar is open
      document.addEventListener("keydown", handleEscape, true);
      // Use a timeout to avoid immediate closing on first click
      setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside, true);
      }, 100);
    }
    return () => {
      // Cleanup listeners
      document.removeEventListener("keydown", handleEscape, true);
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, [open]);

  const handleEscape = (e) => {
    if (e.key === "Escape" && open) {
      setOpen(false);
    }
  };

  const handleClickOutside = (e) => {
    // Don't close if clicking inside the wrapper or calendar elements
    if (
      wrapperRef.current?.contains(e.target) ||
      calendarRef.current?.contains(e.target) ||
      e.target.closest(".rdr-Calendar") ||
      e.target.closest(".rdr-DateRange") ||
      e.target.closest(".rdr-Day") ||
      e.target.closest(".rdr-DayNumber") ||
      e.target.closest(".rdr-DayStartOfWeek") ||
      e.target.closest(".rdr-DayEndOfWeek")
    ) {
      return;
    }

    // When closing, check if we have a valid selection (both dates set)
    // Only trigger if user has started selection and dates are set
    if (
      open &&
      hasStartedSelection &&
      range[0]?.startDate &&
      range[0]?.endDate
    ) {
      const startTime = range[0].startDate.getTime();
      const endTime = range[0].endDate.getTime();

      // If dates are the same (single day selection) and not yet set, trigger onChange
      if (startTime === endTime && !isExplicitlySet) {
        setIsExplicitlySet(true);
        onChange({
          from: getGraphDate(range[0].startDate),
          to: getGraphDate(range[0].endDate),
        });
      }
    }

    // Close the calendar
    if (open) {
      setOpen(false);
    }
  };

  const handleChange = (item) => {
    if (!hasStartedSelection) {
      setHasStartedSelection(true);
      setRange([item.selection]);
      return;
    }
    setRange([item.selection]);

    // Only trigger onChange when both dates are selected and different (complete range)
    if (
      item.selection?.startDate &&
      item.selection?.endDate &&
      item.selection.startDate.getTime() !== item.selection.endDate.getTime()
    ) {
      setIsExplicitlySet(true);
      onChange({
        from: getGraphDate(item.selection.startDate),
        to: getGraphDate(item.selection.endDate),
      });
    }
  };

  return (
    <div ref={wrapperRef} className="donationDateRangeWrapper">
      <TextField
        value={
          isExplicitlySet && range[0]?.startDate && range[0]?.endDate
            ? `${format(range[0].startDate, "MM/dd/yyyy")} to ${format(
                range[0].endDate,
                "MM/dd/yyyy"
              )}`
            : ""
        }
        readOnly
        autoComplete="off"
        inputProps={{
          autoComplete: "off",
          name: "donation-date-range-picker",
        }}
        sx={{
          flex: "1 1 200px",
          width: "13rem",
          input: { color: "rgb(255, 255, 255)" },
          label: { color: "rgb(255, 255, 255)", padding: "0.2rem" },
        }}
        placeholder="Select Date Range"
        label="Date Range"
        className={className}
        onClick={() => {
          // Reset range when opening calendar to show no pre-selection
          if (!open) {
            setRange([
              {
                startDate: undefined,
                endDate: undefined,
                key: "selection",
              },
            ]);
            setHasStartedSelection(false);
          }
          setOpen((prev) => !prev);
        }}
      />

      {open && (
        <div ref={calendarRef} className="donationDateRangeCalendar">
          <DateRange
            onChange={handleChange}
            editableDateInputs={false}
            moveRangeOnFirstSelection={false}
            ranges={
              range[0]?.startDate && range[0]?.endDate ? range : [defaultRange]
            }
            direction="horizontal"
            className="donationCalendarElement"
            preventSnapRefocus={true}
            calendarFocus="backwards"
            showSelectionPreview={false}
          />
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
